import React, { useState, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import ReCAPTCHA from 'react-google-recaptcha';
import { AddressAutocomplete } from '../components/AddressAutocomplete';
import { RegistrationModal } from '../components/RegistrationModal';
import './Register.css';

const RECAPTCHA_SITE_KEY = '6Le1MmcsAAAAALQURwNWLSVXb-SvYUjA0MWtNXD7';

export const Register = () => {
  const { i18n } = useTranslation();
  const { register: registerUser } = useAuth();
  const navigate = useNavigate();
  const recaptchaRef = useRef(null);
  const isGerman = i18n.language === 'de';

  const [formData, setFormData] = useState({
    email: '',
    first_name: '',
    last_name: '',
    mpg_username: '',
    phone_number: '',
    street: '',
    house_no: '',
    zip: '',
    city: '',
    country: '',
    address_additional: '',
    sponsor_name: '',
    shipping_location: 'england',
    ship_to_friend_username: '',
    preferred_lang: isGerman ? 'de' : 'en',
  });

  const [loading, setLoading] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState('');
  const [addressSelected, setAddressSelected] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalData, setModalData] = useState({ message: '', isSuccess: false });
  
  const [errors, setErrors] = useState({
    first_name: '',
    last_name: '',
    mpg_username: '',
    phone_number: '',
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    validateField(name, value);
  };

  const validateField = (name, value) => {
    let error = '';
    
    if (name === 'first_name' || name === 'last_name') {
      if (!/^[a-zA-ZäöüÄÖÜß\\s-]*$/.test(value)) {
        error = isGerman 
          ? 'Nur Buchstaben erlaubt (keine Zahlen oder Sonderzeichen)' 
          : 'Only letters allowed (no numbers or special characters)';
      }
    }
    
    if (name === 'mpg_username') {
      if (!/^[a-zA-Z0-9äöüÄÖÜß]*$/.test(value)) {
        error = isGerman
          ? 'Nur Buchstaben, Zahlen und Umlaute erlaubt (keine Leerzeichen oder Sonderzeichen)'
          : 'Only letters, numbers and umlauts allowed (no spaces or special characters)';
      }
    }
    
    if (name === 'phone_number') {
      if (!/^[0-9+\\s()-]*$/.test(value)) {
        error = isGerman
          ? 'Nur Zahlen, +, Leerzeichen und Klammern erlaubt'
          : 'Only numbers, +, spaces and parentheses allowed';
      }
    }
    
    setErrors({ ...errors, [name]: error });
  };

  const handleRecaptchaChange = (token) => {
    setRecaptchaToken(token);
  };

  const handleAddressSelect = (address) => {
    setFormData({
      ...formData,
      street: address.street,
      house_no: address.house_no,
      zip: address.zip,
      city: address.city,
      country: address.country,
    });
    setAddressSelected(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (errors.first_name || errors.last_name || errors.mpg_username || errors.phone_number) {
      setModalData({
        message: isGerman ? 'Bitte korrigieren Sie die markierten Fehler.' : 'Please correct the marked errors.',
        isSuccess: false
      });
      setModalOpen(true);
      return;
    }

    if (!recaptchaToken) {
      setModalData({
        message: isGerman ? 'Bitte bestätigen Sie, dass Sie kein Roboter sind.' : 'Please confirm you are not a robot.',
        isSuccess: false
      });
      setModalOpen(true);
      return;
    }

    if (!addressSelected) {
      setModalData({
        message: isGerman ? 'Bitte wählen Sie eine gültige Adresse aus den Vorschlägen aus.' : 'Please select a valid address from the suggestions.',
        isSuccess: false
      });
      setModalOpen(true);
      return;
    }

    setLoading(true);

    try {
      const registerData = {
        ...formData,
        address: {
          street: formData.street,
          house_no: formData.house_no,
          zip: formData.zip,
          city: formData.city,
          country: formData.country,
          address_additional: formData.address_additional || null,
        },
        recaptcha_token: recaptchaToken,
      };

      await registerUser(registerData);
      
      setModalData({
        message: isGerman ? 'Ihre Registrierung war erfolgreich!' : 'Your registration was successful!',
        isSuccess: true
      });
      setModalOpen(true);
      
    } catch (error) {
      console.error('Registration error:', error);
      
      let message = 'Registration failed';
      
      if (error.response?.data?.detail) {
        if (typeof error.response.data.detail === 'string') {
          message = error.response.data.detail;
        }
      }
      
      setModalData({ message, isSuccess: false });
      setModalOpen(true);
      recaptchaRef.current?.reset();
      setRecaptchaToken('');
    } finally {
      setLoading(false);
    }
  };

  const handleModalClose = () => {
    setModalOpen(false);
    if (modalData.isSuccess) {
      navigate('/');
    }
  };

  return (
    <div className="register-page">
      <div className="register-container">
        <div className="register-card">
          <h1 className="register-title">{isGerman ? 'Registrieren' : 'Register'}</h1>
          <p className="register-subtitle">{isGerman ? 'Erstellen Sie Ihr Versandkonto' : 'Create your shipping account'}</p>

          <form onSubmit={handleSubmit} className="register-form">
            {/* Personal Information */}
            <div className="form-section">
              <h3 className="section-title">{isGerman ? 'Persönliche Informationen' : 'Personal Information'}</h3>
              
              <div className="form-group">
                <label htmlFor="email">{isGerman ? 'E-Mail-Adresse' : 'Email Address'} *</label>
                <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} required />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="first_name">{isGerman ? 'Vorname' : 'First Name'} *</label>
                  <input 
                    type="text" 
                    id="first_name" 
                    name="first_name" 
                    value={formData.first_name} 
                    onChange={handleChange} 
                    required 
                    className={errors.first_name ? 'input-error' : ''}
                  />
                  {errors.first_name && <span className="error-message">{errors.first_name}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="last_name">{isGerman ? 'Nachname' : 'Last Name'} *</label>
                  <input 
                    type="text" 
                    id="last_name" 
                    name="last_name" 
                    value={formData.last_name} 
                    onChange={handleChange} 
                    required 
                    className={errors.last_name ? 'input-error' : ''}
                  />
                  {errors.last_name && <span className="error-message">{errors.last_name}</span>}
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="mpg_username">{isGerman ? 'MPG Benutzername' : 'MPG Username'} *</label>
                <input 
                  type="text" 
                  id="mpg_username" 
                  name="mpg_username" 
                  value={formData.mpg_username} 
                  onChange={handleChange} 
                  required 
                  placeholder={isGerman ? 'Ihr eindeutiger MPG Benutzername' : 'Your unique MPG Username'}
                  className={errors.mpg_username ? 'input-error' : ''}
                />
                {errors.mpg_username && <span className="error-message">{errors.mpg_username}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="phone_number">{isGerman ? 'Telefonnummer' : 'Phone Number'} *</label>
                <input 
                  type="tel" 
                  id="phone_number" 
                  name="phone_number" 
                  value={formData.phone_number} 
                  onChange={handleChange} 
                  required 
                  placeholder="+49 123 456789"
                  className={errors.phone_number ? 'input-error' : ''}
                />
                {errors.phone_number && <span className="error-message">{errors.phone_number}</span>}
              </div>
            </div>

            {/* Address */}
            <div className="form-section">
              <h3 className="section-title">{isGerman ? 'Versandadresse' : 'Shipping Address'}</h3>
              
              <div className="address-info-box">
                <p>{isGerman ? '🗺️ Geben Sie Ihre Adresse ein und wählen Sie aus den Vorschlägen.' : '🗺️ Enter your address and select from suggestions.'}</p>
              </div>

              <AddressAutocomplete onAddressSelect={handleAddressSelect} />

              {addressSelected && (
                <>
                  <div className="address-preview">
                    <h4>{isGerman ? 'Ausgewählte Adresse:' : 'Selected Address:'}</h4>
                    <p>{String(formData.street)} {String(formData.house_no)}</p>
                    <p>{String(formData.zip)} {String(formData.city)}</p>
                    <p>{String(formData.country)}</p>
                  </div>

                  <div className="form-group">
                    <label htmlFor="address_additional">{isGerman ? 'Adresszusatz (Optional)' : 'Additional Address Info (Optional)'}</label>
                    <input 
                      type="text" 
                      id="address_additional" 
                      name="address_additional" 
                      value={formData.address_additional} 
                      onChange={handleChange}
                      placeholder={isGerman ? 'Etage, Hinweis für Postbote oder spezielle Hausnummer' : 'Floor, Note for postman or special house number'}
                    />
                  </div>
                </>
              )}
            </div>

            {/* Shipping & Additional Info */}
            <div className="form-section">
              <h3 className="section-title">{isGerman ? 'Versandoption' : 'Shipping Option'}</h3>
              
              <div className="form-group">
                <label htmlFor="shipping_location">{isGerman ? 'Versandland' : 'Shipping Country'} *</label>
                <select id="shipping_location" name="shipping_location" value="england" disabled className="shipping-select" required>
                  <option value="england">🇬🇧 England</option>
                </select>
              </div>

              <div className="shipping-info-box england">
                <h4>🇬🇧 {isGerman ? 'Versand aus England' : 'Shipping from England'}</h4>
                <ul>
                  <li>✓ {isGerman ? 'Alle MPG Produkte verfügbar' : 'All MPG products available'}</li>
                  <li>✓ {isGerman ? 'Standard Versandkosten' : 'Standard shipping costs'}</li>
                  <li>✓ {isGerman ? 'Maximales Paketgewicht: 2kg' : 'Maximum package weight: 2kg'}</li>
                </ul>
              </div>
            </div>

            <div className="form-section">
              <h3 className="section-title">{isGerman ? 'Zusätzliche Informationen' : 'Additional Information'}</h3>
              
              <div className="form-group">
                <label htmlFor="sponsor_name">{isGerman ? 'Name des Sponsors' : 'Sponsor Name'} *</label>
                <input type="text" id="sponsor_name" name="sponsor_name" value={formData.sponsor_name} onChange={handleChange} required />
              </div>

              <div className="form-group">
                <label htmlFor="ship_to_friend_username">{isGerman ? 'Versand an Freund (Optional)' : 'Ship to Friend (Optional)'}</label>
                <input 
                  type="text" 
                  id="ship_to_friend_username" 
                  name="ship_to_friend_username" 
                  value={formData.ship_to_friend_username} 
                  onChange={handleChange}
                  placeholder={isGerman ? 'MPG Username des Freundes (leer lassen für eigene Adresse)' : "Friend's MPG Username (leave empty for your address)"}
                />
                <small className="field-hint">
                  {isGerman ? '💡 Pakete werden an die Adresse des Freundes geschickt' : '💡 Packages will be shipped to friend address'}
                </small>
              </div>
            </div>

            {/* reCAPTCHA */}
            <div className="recaptcha-container">
              <ReCAPTCHA ref={recaptchaRef} sitekey={RECAPTCHA_SITE_KEY} onChange={handleRecaptchaChange} />
            </div>

            <button type="submit" className="submit-button" disabled={loading || !recaptchaToken || !addressSelected}>
              {loading ? (isGerman ? 'Lädt...' : 'Loading...') : (isGerman ? 'Registrieren' : 'Register')}
            </button>
          </form>

          <p className="login-link">
            {isGerman ? 'Sie haben bereits ein Konto?' : 'Already have an account?'}{' '}
            <Link to="/login">{isGerman ? 'Hier anmelden' : 'Login here'}</Link>
          </p>
        </div>
      </div>

      <RegistrationModal 
        isOpen={modalOpen}
        onClose={handleModalClose}
        message={modalData.message}
        isSuccess={modalData.isSuccess}
        isGerman={isGerman}
      />
    </div>
  );
};
